<div class="app-dialog employee-manager">
  <header class="app-dialog__header">
    <div class="app-dialog__header-text">
      <h2>{{ 'employeeManager.title' | translate }}</h2>
    </div>
    <button mat-icon-button type="button" (click)="close()" [attr.aria-label]="'cancel' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <div class="app-dialog__body">
    <section class="app-dialog__section">
      <header class="app-dialog__section-head">
        <h3>{{ 'employeeManager.toolbar' | translate }}</h3>
      </header>
      <div class="toolbar-grid">
        <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            [(ngModel)]="searchTerm"
            (input)="onSearchChange()"
            [placeholder]="'employeeManager.searchPlaceholder' | translate"
            [attr.aria-label]="'employeeManager.search' | translate"
          />
        </mat-form-field>

        <div class="toolbar-actions">
          <button mat-raised-button color="primary" type="button" (click)="addNewEmployeeRow()">
            <mat-icon>person_add</mat-icon>
            {{ 'employeeManager.addEmployee' | translate }}
          </button>

          <button mat-stroked-button type="button" (click)="fileInput.click()">
            <mat-icon>upload</mat-icon>
            {{ 'employeeManager.importCsv' | translate }}
          </button>
          <input type="file" accept=".csv,.xlsx,.xls" hidden #fileInput (change)="onFileSelected($event)">

          <button mat-stroked-button type="button" (click)="exportToCsv()" [disabled]="filteredEmployees.length === 0">
            <mat-icon>download</mat-icon>
            {{ 'employeeManager.exportCsv' | translate }}
          </button>

          <button mat-stroked-button type="button" (click)="downloadTemplate()" *ngIf="employees.length === 0">
            <mat-icon>description</mat-icon>
            {{ 'employeeManager.downloadTemplate' | translate }}
          </button>
        </div>
      </div>
    </section>

    <section class="app-dialog__section">
      <header class="app-dialog__section-head">
        <h3>{{ 'employeeManager.employeeTable' | translate }}</h3>
      </header>

      <div class="loading-container" *ngIf="isLoading">
        <mat-spinner diameter="40"></mat-spinner>
        <p>{{ 'employeeManager.loading' | translate }}</p>
      </div>

      <div class="empty-state" *ngIf="!isLoading && filteredEmployees.length === 0">
        <mat-icon>people</mat-icon>
        <h3>{{ 'employeeManager.noEmployees' | translate }}</h3>
        <p>{{ 'employeeManager.noEmployeesDescription' | translate }}</p>
      </div>

      <div class="table-container" *ngIf="!isLoading && filteredEmployees.length > 0">
        <table class="excel-table">
          <thead>
            <tr>
              <th class="action-column">{{ 'employeeManager.actions' | translate }}</th>
              <th *ngFor="let column of availableColumns" [class]="'column-' + column">
                {{ getColumnLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let employee of filteredEmployees; let i = index"
              [class.editing]="employee.isEditing"
              [class.new]="employee.isNew"
              [class.has-errors]="hasAnyValidationErrors(employee)">
              <td class="action-column">
                <div class="action-buttons">
                  <button
                    mat-icon-button
                    color="primary"
                    type="button"
                    (click)="saveEmployee(employee)"
                    [disabled]="!employee.isEditing || employee.isSaving"
                    matTooltip="{{ 'employeeManager.save' | translate }}">
                    <mat-icon *ngIf="!employee.isSaving">save</mat-icon>
                    <mat-spinner *ngIf="employee.isSaving" diameter="20" class="dc-btn-spinner"></mat-spinner>
                  </button>

                  <button
                    mat-icon-button
                    type="button"
                    (click)="cancelEditing(employee)"
                    [disabled]="!employee.isEditing || employee.isSaving"
                    matTooltip="{{ 'employeeManager.cancelEdit' | translate }}">
                    <mat-icon>cancel</mat-icon>
                  </button>

                  <button
                    mat-icon-button
                    color="warn"
                    type="button"
                    (click)="deleteEmployee(employee)"
                    [disabled]="!canDeleteEmployee(employee) || employee.isDeleting"
                    [matTooltip]="!canDeleteEmployee(employee) ? ('employeeManager.cannotDeleteEmployeeWithSignature' | translate) : ('employeeManager.delete' | translate)"
                    *ngIf="isSuperAdmin || (hasRole && employee.dataSource === 'LOCAL')">
                    <mat-icon *ngIf="!employee.isDeleting">delete</mat-icon>
                    <mat-spinner *ngIf="employee.isDeleting" diameter="20" class="dc-btn-spinner"></mat-spinner>
                  </button>
                </div>
              </td>

              <td
                *ngFor="let column of availableColumns"
                [class.editing]="editingCell?.row === i && editingCell?.column === column"
                [class.error]="hasValidationError(employee, column)"
                [class.non-editable]="column === 'email' && !canEditEmail(employee)"
                (click)="startEditing(i, column)"
                (blur)="stopEditing()"
                [matTooltip]="getCellTooltip(employee, column)"
                matTooltipPosition="above">
                <span *ngIf="!(editingCell?.row === i && editingCell?.column === column)">
                  {{ getCellValue(employee, column) || '-' }}
                </span>

                <input
                  *ngIf="editingCell?.row === i && editingCell?.column === column"
                  [value]="getCellValue(employee, column)"
                  (blur)="saveCellValue(i, column, $any($event.target).value); stopEditing()"
                  (keyup.enter)="saveCellValue(i, column, $any($event.target).value); stopEditing()"
                  (keyup.escape)="stopEditing()"
                  [class.error-input]="hasValidationError(employee, column)"
                  class="cell-input"
                  autofocus>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>

  <footer class="app-dialog__footer">
    <button mat-stroked-button type="button" (click)="close()">
      {{ 'cancel' | translate }}
    </button>
    <button
      mat-stroked-button
      color="primary"
      type="button"
      (click)="saveAndInstallAllChanges()"
      [disabled]="!hasUnsavedChanges() || isSavingAll || hasValidationErrorsInUnsaved()"
      [matTooltip]="hasValidationErrorsInUnsaved() ? ('employeeManager.cannotSaveWithErrors' | translate) : ''">
      <mat-spinner *ngIf="isSavingAll" diameter="20" class="dc-btn-spinner"></mat-spinner>
      <mat-icon *ngIf="!isSavingAll">install_desktop</mat-icon>
      <span>{{ isSavingAll ? ('saving' | translate) : ('employeeManager.saveAndInstallAllChanges' | translate) }}</span>
    </button>
    <button
      mat-raised-button
      color="primary"
      type="button"
      (click)="saveAllChanges()"
      [disabled]="!hasUnsavedChanges() || isSavingAll || hasValidationErrorsInUnsaved()"
      [matTooltip]="hasValidationErrorsInUnsaved() ? ('employeeManager.cannotSaveWithErrors' | translate) : ''">
      <mat-spinner *ngIf="isSavingAll" diameter="20" class="dc-btn-spinner"></mat-spinner>
      <mat-icon *ngIf="!isSavingAll">save</mat-icon>
      <span>{{ isSavingAll ? ('saving' | translate) : ('employeeManager.saveAllChanges' | translate) }}</span>
    </button>
  </footer>
</div>
